import { useEffect, useState } from "react";
import { DocumentPreview } from "./DocumentPreview";
import { readBlob } from "@/lib/opfs";
import { X, Download, FileText, Calendar, HardDrive, Tag } from "lucide-react";
import type { Document as DocType } from "@/types";

interface DocumentDetailProps {
  document: DocType;
  onClose: () => void;
}

export function DocumentDetail({ document, onClose }: DocumentDetailProps) {
  const [occurrences, setOccurrences] = useState<{ senderEmail?: string; subject?: string; receivedAt?: string }[]>([]);

  useEffect(() => {
    import("@/lib/db").then(({ db }) => {
      db.occurrences.where({ documentId: document.id }).toArray().then((occs) => {
        setOccurrences(occs.map((o) => ({
          senderEmail: o.senderEmail,
          subject: o.subject,
          receivedAt: o.receivedAt,
        })));
      });
    });
  }, [document.id]);

  const handleDownload = async () => {
    if (!document.opfsPath) return;
    const blob = await readBlob(document.opfsPath);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = document.canonicalName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatDate = (d?: string) => {
    if (!d) return "Unknown";
    return new Date(d).toLocaleDateString();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-2xl bg-surface-0 p-6 shadow-xl dark:bg-dark-surface-1 border border-border-default dark:border-dark-border-default">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="truncate pr-4 text-lg font-bold text-text-primary dark:text-dark-text-primary">
            {document.canonicalName}
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-text-muted hover:bg-surface-2 hover:text-text-secondary dark:text-dark-text-muted dark:hover:bg-dark-surface-2 dark:hover:text-dark-text-secondary transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Preview */}
        <div className="mt-4 aspect-video overflow-hidden rounded-xl bg-surface-2 dark:bg-dark-surface-2 border border-border-default dark:border-dark-border-default">
          <DocumentPreview document={document} className="h-full w-full" />
        </div>

        {/* Metadata grid */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="flex items-start gap-2.5 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3">
            <Tag size={16} className="text-text-muted mt-0.5" />
            <div>
              <p className="text-xs text-text-muted">Type</p>
              <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">{document.mimeType}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3">
            <HardDrive size={16} className="text-text-muted mt-0.5" />
            <div>
              <p className="text-xs text-text-muted">Size</p>
              <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">
                {document.sizeBytes > 0
                  ? `${(document.sizeBytes / 1024 / 1024).toFixed(2)} MB`
                  : "Unknown"}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3">
            <FileText size={16} className="text-text-muted mt-0.5" />
            <div>
              <p className="text-xs text-text-muted">Extension</p>
              <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">{document.extension || "—"}</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3">
            <Calendar size={16} className="text-text-muted mt-0.5" />
            <div>
              <p className="text-xs text-text-muted">Added</p>
              <p className="text-sm font-medium text-text-primary dark:text-dark-text-primary">{formatDate(document.createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Occurrences */}
        {occurrences.length > 0 && (
          <div className="mt-5">
            <p className="text-sm font-semibold text-text-primary dark:text-dark-text-primary mb-2">From email</p>
            <div className="space-y-2">
              {occurrences.map((occ, i) => (
                <div key={i} className="rounded-lg bg-surface-1 border border-border-default p-3 text-sm dark:bg-dark-surface-2 dark:border-dark-border-default">
                  <p className="font-medium text-text-primary dark:text-dark-text-primary">{occ.subject}</p>
                  <p className="text-xs text-text-muted mt-0.5">{occ.senderEmail} · {formatDate(occ.receivedAt)}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={handleDownload}
            className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
          >
            <Download size={16} />
            Download
          </button>
          <button
            onClick={onClose}
            className="rounded-md border border-border-strong bg-surface-0 px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-2 dark:border-dark-border-strong dark:bg-dark-surface-1 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
